"use client";

import AuthCard from "@booking/core-ui/admin/components/AuthCard";
import { Suspense, useEffect, useState } from "react";
import { getPKCE, clearPKCE, saveTokens } from "@/lib/auth";
import { api } from "@/lib/api";
import { useTranslation, useLangRouter } from "@/lib/i18n/context";
import { useSearchParams } from "@booking/core-ui/router";
import { LOGOUT_STATE } from "@booking/core-ui/auth";
import Button from "@mui/material/Button";

function AuthCallbackInner() {
  const router = useLangRouter();
  const searchParams = useSearchParams();
  const [error, setError] = useState("");
  const { t, langHref } = useTranslation();

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const errorParam = searchParams.get("error");

    if (errorParam) {
      const errorMsg = searchParams.get("error_description") || errorParam;
      setError(errorMsg);
      return;
    }

    // The post-logout return. post_logout_redirect_uri must match a registered
    // redirect_uri exactly and /auth/callback is the only one, so a completed
    // sign-out lands here too — with no code. LOGOUT_STATE is what separates
    // that from a genuinely malformed authorization redirect.
    if (!code && state === LOGOUT_STATE) {
      clearPKCE();
      window.location.href = langHref("/login");
      return;
    }

    if (!code || !state) {
      setError(t("missingAuthCode"));
      return;
    }

    const pkce = getPKCE();
    if (!pkce || pkce.state !== state) {
      clearPKCE();
      setError(t("invalidState"));
      return;
    }

    const redirectUri = `${window.location.origin}/auth/callback`;

    api.auth
      .callback(code, pkce.codeVerifier, redirectUri)
      .then((tokens) => {
        clearPKCE();
        saveTokens(tokens);
        window.location.href = langHref("/dashboard");
      })
      .catch((err) => {
        const errorMsg = err instanceof Error ? err.message : t("authFailed");
        clearPKCE();
        setError(errorMsg);
      });
  }, [searchParams, router, t]);

  if (error) {
    return (
      <AuthCard title={t("authFailedTitle")} subtitle={error}>
        <Button variant="contained" fullWidth component="a" href={langHref("/login")}>
          {t("tryAgain")}
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t("authenticating")} subtitle={t("pleaseWaitSignIn")} />
  );
}

export default function AuthCallbackPage() {
  const { t } = useTranslation();
  return (
    <Suspense fallback={<AuthCard title={t("authenticating")} subtitle={t("pleaseWait")} />}>
      <AuthCallbackInner />
    </Suspense>
  );
}
